import { gsap } from 'gsap';
import { SplitText } from 'gsap/SplitText';
import { themeColor } from '../utils/theme-color';

gsap.registerPlugin( SplitText );

const GRID = 14; // px — trail square size, kept small and hard-edged for a pixel-art look.
const LIFESPAN = 550; // ms a trail square takes to fade out after being spawned.
const COLOR_DURATION = 0.15; // s — h1 letter color tween, in and out.

/**
 * Creates one grid-snapped trail square and appends it to `layer`.
 * @param {HTMLElement} layer Trail container to append the square to.
 * @param {number}      x     Section-relative x, already snapped to GRID.
 * @param {number}      y     Section-relative y, already snapped to GRID.
 * @param {string}      color CSS color for the square's background.
 * @return {Object} { el, x, y, born } — born is used to fade/expire it.
 */
function spawnCell( layer, x, y, color ) {
	const el = document.createElement( 'div' );
	el.className = 'absolute';
	el.style.left = `${ x }px`;
	el.style.top = `${ y }px`;
	el.style.width = `${ GRID }px`;
	el.style.height = `${ GRID }px`;
	el.style.backgroundColor = color;
	layer.appendChild( el );
	return { el, x, y, born: window.performance.now() };
}

// Whether any still-live trail cell's viewport rect overlaps a char's.
function isUnderTail( charRect, cellRects ) {
	return cellRects.some(
		( rect ) =>
			charRect.left < rect.right &&
			charRect.right > rect.left &&
			charRect.top < rect.bottom &&
			charRect.bottom > rect.top
	);
}

/**
 * Pixelated mouse-trail effect: a grid of fading squares in `tailColor`
 * follows the pointer within `sectionEl`.
 *
 * On the hero section specifically, which carries a
 * `[data-gsap="pixel-trail-heading"]` h1, the trail layer sits at -z-10,
 * *behind* the section's in-flow content, so the squares themselves never
 * visually paint over the h1's glyphs — instead, this also swaps that h1's
 * letters from orange to a second theme color while they're under the
 * (still-fading) trail, driven by a rect-overlap check against
 * getBoundingClientRect each frame, so it stays correct across scroll.
 *
 * Every other section has no such letter-swap effect to protect, and is
 * typically dense enough (photos, opaque cards, a full-width marquee) that
 * a behind-content trail would be invisible under most of the hover area —
 * see the housingStatement/eventsList bug this was fixed for. So there the
 * layer instead sits at z-10, *above* the content, which is what actually
 * makes the trail visible while hovering.
 * @param {HTMLElement} sectionEl The <section> to attach the trail to.
 * @param {string}      tailColor CSS color for the trail squares.
 */
export function initPixelCursorTrail( sectionEl, tailColor ) {
	if ( ! sectionEl ) {
		return;
	}

	if ( window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches ) {
		return;
	}

	sectionEl.classList.add( 'relative' );

	const h1 = sectionEl.querySelector( '[data-gsap="pixel-trail-heading"]' );

	const trailLayer = document.createElement( 'div' );
	trailLayer.className = h1
		? 'absolute inset-0 -z-10 overflow-hidden pointer-events-none'
		: 'absolute inset-0 z-10 overflow-hidden pointer-events-none';
	sectionEl.appendChild( trailLayer );

	let cells = [];

	sectionEl.addEventListener( 'mousemove', ( e ) => {
		const sectionRect = sectionEl.getBoundingClientRect();
		const x = Math.floor( ( e.clientX - sectionRect.left ) / GRID ) * GRID;
		const y = Math.floor( ( e.clientY - sectionRect.top ) / GRID ) * GRID;

		const existing = cells.find( ( cell ) => cell.x === x && cell.y === y );

		if ( existing ) {
			existing.born = window.performance.now();
			return;
		}

		cells.push( spawnCell( trailLayer, x, y, tailColor ) );
	} );

	let chars = [];
	let revealColor;
	let baseColor;

	if ( h1 ) {
		revealColor = themeColor( '--color-green', '#3f783f' );
		baseColor = themeColor( '--color-orange', '#e3572a' );

		// Wait for webfonts: SplitText's char metrics (and therefore hit
		// boxes) depend on the rendered font, not a fallback.
		document.fonts.ready.then( () => {
			SplitText.create( h1, {
				// Also split into words (not just chars) so each word's chars
				// stay wrapped in one inline-block container — without it, the
				// browser can insert a line break between any two adjacent
				// char spans, splitting words mid-way.
				type: 'chars, words',
				autoSplit: true,
				onSplit( self ) {
					chars = self.chars.map( ( el ) => ( {
						el,
						revealed: false,
					} ) );
				},
			} );
		} );
	}

	function tick() {
		const now = window.performance.now();
		const sectionRect = sectionEl.getBoundingClientRect();

		cells = cells.filter( ( cell ) => {
			const age = now - cell.born;

			if ( age >= LIFESPAN ) {
				cell.el.remove();
				return false;
			}

			cell.el.style.opacity = String( 1 - age / LIFESPAN );
			return true;
		} );

		if ( chars.length ) {
			const cellRects = cells.map( ( cell ) => ( {
				left: sectionRect.left + cell.x,
				top: sectionRect.top + cell.y,
				right: sectionRect.left + cell.x + GRID,
				bottom: sectionRect.top + cell.y + GRID,
			} ) );

			chars.forEach( ( char ) => {
				const under = isUnderTail(
					char.el.getBoundingClientRect(),
					cellRects
				);

				if ( under === char.revealed ) {
					return;
				}

				char.revealed = under;

				if ( under ) {
					gsap.to( char.el, {
						color: revealColor,
						duration: COLOR_DURATION,
						overwrite: true,
					} );
				} else {
					// Tween to the explicit orange first, then clear the
					// inline style so it falls back to the inherited
					// .text-orange class — same value, no visible jump.
					gsap.to( char.el, {
						color: baseColor,
						duration: COLOR_DURATION,
						overwrite: true,
						onComplete: () => {
							char.el.style.color = '';
						},
					} );
				}
			} );
		}

		window.requestAnimationFrame( tick );
	}

	window.requestAnimationFrame( tick );
}
